import React, { useState, useMemo } from 'react';
import { Card, Button, Input } from '../ui';
import { Calculator, Users, Plus, Shield, Check, RefreshCw } from 'lucide-react';

interface GiftTier {
  percent: number;
  qtyNeeded: number;
  label: string;
}

const GIFT_CHART_MODEL: GiftTier[] = [
  { percent: 0.15, qtyNeeded: 1, label: 'Lead Gift' },
  { percent: 0.05, qtyNeeded: 4, label: 'Major Gifts' },
  { percent: 0.02, qtyNeeded: 10, label: 'Special Gifts' },
  { percent: 0.01, qtyNeeded: 20, label: 'Supporting Gifts' }
];

export function ChurchCapitalCampaign() {
  const [targetGoal, setTargetGoal] = useState('250000');
  const [familiesCount, setFamiliesCount] = useState('200');
  const [avgAttendance, setAvgAttendance] = useState('150');

  const goal = parseFloat(targetGoal) || 50000;
  const families = parseInt(familiesCount) || 1;
  const attendance = parseInt(avgAttendance) || 1;

  // Feasibility Scoring Model
  // Proposes a standard church annual giving of $1,500 per family.
  // Standard capital campaign is feasible if goal is less than 3x the annual giving total.
  const estAnnualGiving = families * 1800;
  const goalToGivingRatio = estAnnualGiving > 0 ? goal / estAnnualGiving : 0;

  const feasibilityRating = useMemo(() => {
    if (goalToGivingRatio <= 1.5) return { rating: 'HIGH FEASIBILITY', color: 'text-green-600', note: 'The campaign target is well within standard giving ranges (< 1.5x annual budget).' };
    if (goalToGivingRatio <= 3.0) return { rating: 'MODERATE FEASIBILITY', color: 'text-[#0044FF]', note: 'Feasible with a strong leadership gift phase (goal is 1.5x - 3x annual budget).' };
    return { rating: 'CHALLENGING / LEVERAGE REQUIRED', color: 'text-red-600', note: 'Requires major external sponsors or multi-year pledge expansions (> 3x annual budget).' };
  }, [goalToGivingRatio]);

  // Generate Gift Chart Rows
  const giftChartRows = useMemo(() => {
    const rows = GIFT_CHART_MODEL.map((tier, idx) => {
      const giftSize = goal * tier.percent;
      const totalForTier = giftSize * tier.qtyNeeded;

      return {
        ...tier,
        giftSize,
        totalForTier,
        prospectsNeeded: tier.qtyNeeded * 4 // Standard rule: 4 prospects needed per gift
      };
    });

    // Base support tier handles the remainder of the goal
    const accumulatedSum = rows.reduce((sum, r) => sum + r.totalForTier, 0);
    const remainder = Math.max(0, goal - accumulatedSum);
    const baseGiftSize = 500; // Average base gift
    const baseQty = Math.ceil(remainder / baseGiftSize);

    rows.push({
      percent: remainder / goal,
      qtyNeeded: baseQty,
      label: 'Broad Base Support',
      giftSize: baseGiftSize,
      totalForTier: remainder,
      prospectsNeeded: baseQty * 2
    });

    return rows;
  }, [goal]);

  const loadSample = () => {
    setTargetGoal('250000');
    setFamiliesCount('200');
    setAvgAttendance('150');
  };

  const clearAll = () => {
    setTargetGoal('0');
    setFamiliesCount('0');
    setAvgAttendance('0');
  };

  return (
    <div className="space-y-8">
      {/* Intro Hero */}
      <div className="bg-[#FFD600] border-4 border-black text-black p-6 shadow-[4px_4px_0px_#000000]">
        <div className="inline-block bg-black text-white px-2 py-0.5 text-[10px] font-mono font-bold uppercase tracking-widest mb-2">
          Capital Campaign Gift Table & Feasibility Calculator
        </div>
        <h3 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight mb-2">
          Church Capital Campaign & Gift Range Modeler
        </h3>
        <p className="font-bold text-sm max-w-2xl">
          Evaluate congregational capacity, calculate donor giving pyramid tiers, and generate an actionable Gift Distribution Table with prospect multipliers to hit building fund targets.
        </p>
        <div className="flex flex-wrap gap-3 mt-4">
          <Button onClick={loadSample} variant="secondary" className="bg-black text-white hover:bg-[#0044FF] border-2 border-black rounded-none uppercase font-mono tracking-widest text-[10px] font-bold">
            <RefreshCw size={12} className="mr-2" /> Load Demo
          </Button>
          <Button onClick={() => window.print()} variant="secondary" className="bg-white text-black hover:bg-black hover:text-white border-2 border-black rounded-none uppercase font-mono tracking-widest text-[10px] font-bold">
            Print Gift Table
          </Button>
          <Button onClick={clearAll} variant="ghost" className="border-2 border-black text-black hover:bg-red-600 hover:text-white rounded-none uppercase font-mono tracking-widest text-[10px] font-bold">
            Reset
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8 text-sm">
        
        {/* Settings Form */}
        <div className="lg:col-span-1 bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4">
          <h4 className="font-display font-black text-xl uppercase tracking-tight">Campaign Parameters</h4>
          
          <div className="space-y-4 font-bold">
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Target Fundraising Goal ($)</label>
              <Input type="number" value={targetGoal} onChange={e => setTargetGoal(e.target.value)} className="border-2 border-black rounded-none h-10 font-mono font-bold" />
            </div>

            <div>
              <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Active Congregation Families</label>
              <Input type="number" value={familiesCount} onChange={e => setFamiliesCount(e.target.value)} className="border-2 border-black rounded-none h-10 font-mono font-bold" />
            </div>

            <div>
              <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Average Weekly Attendance</label>
              <Input type="number" value={avgAttendance} onChange={e => setAvgAttendance(e.target.value)} className="border-2 border-black rounded-none h-10 font-mono font-bold" />
            </div>

            <div className="pt-4 border-t-2 border-black font-mono text-xs text-gray-500 space-y-2">
              <p>Estimated Annual Giving: <strong className="text-black dark:text-white">${estAnnualGiving.toLocaleString()}</strong></p>
              <p>Goal to Giving Ratio: <strong className="text-black dark:text-white">{goalToGivingRatio.toFixed(2)}x</strong></p>
            </div>

          </div>
        </div>

        {/* Output Metrics & Gift Table */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Feasibility Indicator */}
          <div className="border-4 border-black p-6 bg-white dark:bg-black flex flex-col sm:flex-row items-center justify-between gap-6 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
            <div>
              <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-gray-500 block">Feasibility Rating</span>
              <h4 className={`font-display font-black text-2xl uppercase tracking-tight mt-1 ${feasibilityRating.color}`}>
                {feasibilityRating.rating}
              </h4>
            </div>
            <p className="text-xs text-gray-500 font-mono max-w-xs leading-normal sm:text-right border-l-4 sm:border-l-0 sm:border-r-4 border-[#0044FF] pl-3 sm:pl-0 sm:pr-3">
              {feasibilityRating.note}
            </p>
          </div>

          {/* Gift Chart Table */}
          <div className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4">
             <h4 className="font-display font-black text-xl uppercase tracking-tight border-b-4 border-black pb-2 dark:border-white">
               Standard Gift Distribution Chart
             </h4>
             <p className="text-xs text-gray-500 font-mono leading-normal">
               Capital campaigns typically rely on a proportional gift tree model. To secure a gift, you generally need to identify 4 prospects.
             </p>

             <div className="overflow-x-auto pt-2">
               <table className="w-full text-left text-xs border-collapse">
                 <thead>
                   <tr className="bg-[#F5F5F0] border-b-4 border-black font-mono font-bold uppercase tracking-wider text-black dark:bg-slate-900 dark:border-white dark:text-white">
                     <th className="py-2 px-3">Donation Tier</th>
                     <th className="py-2 px-3 text-right">Individual Gift Size</th>
                     <th className="py-2 px-3 text-center">Gifts Needed</th>
                     <th className="py-2 px-3 text-center">Prospects Needed (4x)</th>
                     <th className="py-2 px-3 text-right">Total Tier Value</th>
                   </tr>
                 </thead>
                 <tbody className="divide-y-2 divide-gray-200 dark:divide-slate-800 font-bold font-mono">
                   {giftChartRows.map((row, idx) => (
                     <tr key={idx} className="hover:bg-[#FFD600]/10 transition-colors">
                       <td className="py-2.5 px-3 font-display uppercase tracking-tight text-xs text-black dark:text-white">
                         {row.label}
                       </td>
                       <td className="py-2.5 px-3 text-right">
                         ${Math.round(row.giftSize).toLocaleString()}
                       </td>
                       <td className="py-2.5 px-3 text-center">
                         {row.qtyNeeded}
                       </td>
                       <td className="py-2.5 px-3 text-center text-slate-500">
                         {row.prospectsNeeded}
                       </td>
                       <td className="py-2.5 px-3 text-right text-base text-[#0044FF]">
                         ${Math.round(row.totalForTier).toLocaleString()}
                       </td>
                     </tr>
                   ))}
                 </tbody>
               </table>
             </div>
          </div>

        </div>

      </div>

    </div>
  );
}
